/**
 * Scheme and permission book-keeping routes.
 * Mounted at /app/maud-erp/scheme-applications.
 *
 * The index route is the combined ledger across LRS, BPS, APDPMS, Bhoo Konugolu
 * and GPBP — what arrived, whether it reconciled, and the raw JSON behind any
 * application. It is unchanged and remains the landing screen.
 *
 * The dashboard routes are per-scheme analytical views. They are separate
 * routes rather than another tab on the index because they answer a different
 * question: the index is "what did they send us and did it balance", the
 * dashboards are "what does it tell us". They also get their own sidebar
 * entries, which a tab cannot.
 *
 * `gpbp` is a dashboard even though nothing has been pushed yet — the opposite
 * of the call made for `bhookonugolu` below, and deliberately so. Its screen is
 * explicitly a demonstration, labelled as such in three places, and its purpose
 * is to be reviewed and signed off by DTCP before Entro Labs write any
 * integration code. A specification gets argued about; a screen gets agreed.
 *
 * `bhookonugolu` is now one of them, on the same reasoning as `gpbp` and
 * reversing the earlier call. It previously opened the ledger pre-filtered to
 * the scheme, on the grounds that there was no auction data to shape a dashboard
 * against. That is still true — the portal has pushed nothing — but "wait for
 * data" was the wrong conclusion: the state-level audience for this book is the
 * CM's review and the minister's office, and what they need from a land-auction
 * feed has to be agreed BEFORE the portal team builds the push, not after. So it
 * is a demonstration dashboard, labelled as such in three places, and the URL it
 * answers on has not changed.
 */

import { lazy } from 'react';
import type { RouteObject } from 'react-router-dom';

const SchemeBookkeeping = lazy(() => import('../pages/SchemeBookkeeping'));
const DpmsDashboard = lazy(() => import('../pages/DpmsDashboard'));
const LrsDashboard = lazy(() => import('../pages/LrsDashboard'));
const BpsDashboard = lazy(() => import('../pages/BpsDashboard'));
const GpbpDashboard = lazy(() => import('../pages/GpbpDashboard'));
const BhookDashboard = lazy(() => import('../pages/BhookDashboard'));

export const schemeBookkeepingRoutes: RouteObject[] = [
  { index: true, element: <SchemeBookkeeping /> },
  // Alias so the ledger can have its own sidebar entry beside the dashboards.
  // The parent menu row expands rather than navigates, so the group needs a
  // child that actually opens the ledger.
  { path: 'all', element: <SchemeBookkeeping /> },
  { path: 'dpms', element: <DpmsDashboard /> },
  { path: 'lrs', element: <LrsDashboard /> },
  { path: 'bps', element: <BpsDashboard /> },
  { path: 'gpbp', element: <GpbpDashboard /> },
  { path: 'bhookonugolu', element: <BhookDashboard /> },
];

export default schemeBookkeepingRoutes;
